import { useState, type FormEvent } from "react";
import emailjs from "@emailjs/browser";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import { siteInfo } from "@/mocks/site";

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    const formData = new FormData(form);
    if (honeypot) formData.delete("phone_alt");

    try {
      await fetch("https://readdy.ai/api/form/d8tjdj4al24muhn2r84g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
    } catch {
      // silently handle
    }

    emailjs.sendForm("service_ml5rlis", "template_hzrsu2e", form).catch(() => {});

    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <SiteLayout navVariant="solid">
        <PageBanner
          title="문의하기"
          subtitle="문의가 접수되었습니다"
          breadcrumb={[{ label: "문의하기" }]}
          bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20office%20interior%20with%20warm%20lighting%2C%20professional%20business%20environment%2C%20organized%20workspace%2C%20corporate%20atmosphere&width=1600&height=680&seq=krubber-contact-banner&orientation=landscape"
        />
        <section className="py-20 bg-background-50">
          <div className="max-w-[640px] mx-auto px-6 text-center">
            <span className="w-20 h-20 mx-auto rounded-2xl bg-primary-100 flex items-center justify-center">
              <i className="ri-check-line text-primary-600 text-3xl" />
            </span>
            <h2 className="mt-6 text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
              문의가 접수되었습니다
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              소중한 문의 감사드립니다. 담당자가 확인 후 빠른 시일 내에 회신 드리겠습니다.
              긴급한 사항은 <strong className="text-foreground-950">{siteInfo.tel}</strong>로 연락 부탁드립니다.
            </p>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title="문의하기"
        subtitle="제품 문의, 견적 요청, 기술 상담 등 무엇이든 문의해 주세요"
        breadcrumb={[{ label: "문의하기" }]}
        bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20office%20interior%20with%20warm%20lighting%2C%20professional%20business%20environment%2C%20organized%20workspace%2C%20corporate%20atmosphere&width=1600&height=680&seq=krubber-contact-banner&orientation=landscape"
      />
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            <div className="lg:w-[480px] flex-shrink-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                연락처 안내
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                전화 또는 이메일로 직접 문의하셔도 됩니다. 아래 연락처로 편하게 연락 주세요.
              </p>
              <div className="mt-8 flex flex-col gap-4">
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-600 text-lg" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">전화</p>
                    <a href={siteInfo.telLink} className="text-lg font-heading font-extrabold text-foreground-950 hover:text-primary-600 transition-colors cursor-pointer">
                      {siteInfo.tel}
                    </a>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-printer-line text-primary-600 text-lg" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">팩스</p>
                    <p className="text-lg font-heading font-extrabold text-foreground-950">{siteInfo.fax}</p>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600 text-lg" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">이메일</p>
                    <a href={siteInfo.emailLink} className="text-lg font-heading font-extrabold text-foreground-950 hover:text-primary-600 transition-colors cursor-pointer">
                      {siteInfo.email}
                    </a>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-line text-primary-600 text-lg" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">주소</p>
                    <p className="text-sm font-semibold text-foreground-900 leading-relaxed">{siteInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                온라인 문의
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                아래 양식을 작성해 주시면 담당자가 확인 후 연락 드립니다.
              </p>
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-8 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="company" className="block text-xs font-bold text-foreground-600 mb-2">
                      회사명 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="회사명을 입력하세요"
                    />
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-xs font-bold text-foreground-600 mb-2">
                      담당자명 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="담당자명을 입력하세요"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold text-foreground-600 mb-2">
                      연락처 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="연락처를 입력하세요"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold text-foreground-600 mb-2">
                      이메일 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="이메일을 입력하세요"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="inquiry_type" className="block text-xs font-bold text-foreground-600 mb-2">
                    문의 유형
                  </label>
                  <select
                    id="inquiry_type"
                    name="inquiry_type"
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                  >
                    <option value="">문의 유형을 선택하세요</option>
                    <option value="product">제품 문의</option>
                    <option value="quotation">견적 요청</option>
                    <option value="technical">기술 상담</option>
                    <option value="sample">샘플 요청</option>
                    <option value="other">기타</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs font-bold text-foreground-600 mb-2">
                    문의 내용 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder="문의하실 내용을 구체적으로 입력해 주세요. (제품 용도, 필요 수량, 적용 환경 등)"
                  />
                  <p className="mt-1 text-xs text-foreground-400">최대 500자까지 입력 가능합니다.</p>
                </div>
                <div>
                  <label htmlFor="attachment" className="block text-xs font-bold text-foreground-600 mb-2">
                    첨부파일 (도면, 사진 등)
                  </label>
                  <input
                    type="file"
                    id="attachment"
                    name="attachment"
                    className="w-full text-sm text-foreground-600 file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200 file:cursor-pointer"
                  />
                  <p className="mt-1 text-xs text-foreground-400">
                    PDF, JPG, PNG, DWG 파일 첨부 가능 (최대 10MB)
                  </p>
                </div>

                <div className="flex items-start gap-3 mt-2">
                  <input
                    type="checkbox"
                    id="privacy_consent"
                    name="privacy_consent"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="privacy_consent" className="text-xs text-foreground-600 leading-relaxed">
                    개인정보 수집 및 이용에 동의합니다. 수집된 개인정보는 문의 응대 목적으로만 사용되며,
                    관련 법령에 따라 안전하게 보관됩니다. <span className="text-accent-500">*</span>
                  </label>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hp-shield"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-4 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin" />
                      접수 중...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      문의 접수하기
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}